import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import type { MemoryDistribution, MemoryPoolDistribution } from '@root/models';
import { MemoryDonut, MEMORY_SEGMENT_COLORS, type MemoryDonutSegment } from './MemoryDonut';
import { ImgGpu } from '@images/ImgGpu';
import { ImgRam } from '@images/ImgRam';
import { ImgWarning } from '@images/ImgWarning';

export interface RequiredSpace {
  label: string;
  sizeGb: number;
}

interface MemoryDistributionPopupProps {
  distribution: MemoryDistribution;
  /** "Assumed space" for the pending operation (e.g. selected image workflow) — shown as an extra segment on the GPU donut. */
  required?: RequiredSpace | null;
  anchor: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}

function fmtGb(gb: number): string {
  return gb >= 100 ? gb.toFixed(0) : gb >= 10 ? gb.toFixed(1) : gb.toFixed(2);
}

function fmtContext(tokens?: number | null): string | null {
  if (!tokens) return null;
  return tokens >= 1024 ? `${(tokens / 1024).toFixed(1)}k` : `${tokens}`;
}

/** Groups segments under per-model headers with a subtotal; model-less segments stay individual. */
type LegendEntry =
  | { type: 'header'; key: string; model: string; totalGb: number }
  | { type: 'item'; seg: MemoryDonutSegment };

function groupLegendSegments(segments: MemoryDonutSegment[]): LegendEntry[] {
  const entries: LegendEntry[] = [];
  let currentModel: string | null = null;
  for (const seg of segments) {
    if (seg.model) {
      if (seg.model !== currentModel) {
        currentModel = seg.model;
        const totalGb = segments.filter(s => s.model === seg.model).reduce((s, x) => s + x.sizeGb, 0);
        entries.push({ type: 'header', key: `hdr-${seg.model}`, model: seg.model, totalGb });
      }
      entries.push({ type: 'item', seg });
    } else {
      currentModel = null;
      entries.push({ type: 'item', seg });
    }
  }
  return entries;
}

interface PoolView {
  title: string;
  icon: React.ReactNode;
  pool: MemoryPoolDistribution;
  segments: MemoryDonutSegment[];
  required?: { seg: MemoryDonutSegment; fits: boolean };
}

/** Build a donut view for one pool, optionally adding the "required" segment. */
function buildView(pool: MemoryPoolDistribution, title: string, icon: React.ReactNode, required?: RequiredSpace | null): PoolView {
  const segments: MemoryDonutSegment[] = [...pool.segments];
  let requiredView: PoolView['required'];

  if (required && required.sizeGb > 0) {
    const freeGb = Math.max(0, pool.totalGb - pool.usedGb);
    const fits = required.sizeGb <= freeGb;
    requiredView = {
      fits,
      seg: {
        key: 'required',
        label: required.label,
        group: 'required',
        sizeGb: required.sizeGb,
        color: fits ? MEMORY_SEGMENT_COLORS.required : MEMORY_SEGMENT_COLORS.requiredOver,
      },
    };
    // Draw the required space: full size when it fits (inside the free area);
    // capped at the free area (red) when it overflows.
    segments.push({
      ...requiredView.seg,
      sizeGb: fits ? required.sizeGb : Math.min(required.sizeGb, Math.max(freeGb, 0.05)),
    });
  }

  return { title, icon, pool, segments, required: requiredView };
}

@observer
class MemoryDistributionPopup extends Component<MemoryDistributionPopupProps> {
  /** Matches .memory-dist-popup width in CSS. */
  private static readonly POPUP_WIDTH = 470;
  /** First-paint height estimate; ResizeObserver corrects it. */
  private static readonly ESTIMATED_HEIGHT = 300;
  private static readonly MARGIN = 8;
  private static readonly GAP = 10;

  @observable private _hoverKey: string | null = null;
  @observable private _pos: { top: number; left: number } = { top: 8, left: 8 };
  private _popupRef: React.RefObject<HTMLDivElement | null> = React.createRef();
  private _lastHoverKey: string | null = null;
  private _resizeObserver: ResizeObserver | null = null;

  constructor(props: MemoryDistributionPopupProps) {
    super(props);
    makeObservable(this);
    // Anchor before first paint to avoid a top-left flash.
    this._pos = this._computePosition(MemoryDistributionPopup.ESTIMATED_HEIGHT)
      ?? { top: Math.max(8, (window.innerHeight - MemoryDistributionPopup.ESTIMATED_HEIGHT) / 2), left: Math.max(8, (window.innerWidth - MemoryDistributionPopup.POPUP_WIDTH) / 2) };
  }

  componentDidMount() {
    if (typeof ResizeObserver !== 'undefined' && this._popupRef.current) {
      this._resizeObserver = new ResizeObserver(() => {
        this._reanchor();
      });
      this._resizeObserver.observe(this._popupRef.current);
    }
    window.addEventListener('resize', this._onResize);
    document.addEventListener('mousedown', this._onDocumentMouseDown);
    document.addEventListener('keydown', this._onKeyDown);
  }

  componentDidUpdate(prevProps: MemoryDistributionPopupProps) {
    if (prevProps.distribution !== this.props.distribution) {
      this._reanchor();
    }
    if (this._hoverKey !== this._lastHoverKey) {
      const changed = this._hoverKey;
      this._lastHoverKey = this._hoverKey;
      if (changed) {
        this._popupRef.current?.querySelector(`.memory-dist-legend-item[data-key="${CSS.escape(changed)}"]`)
          ?.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  componentWillUnmount() {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('mousedown', this._onDocumentMouseDown);
    document.removeEventListener('keydown', this._onKeyDown);
  }

  private _onResize = () => {
    this._reanchor();
  };

  /** Returns null when the anchor isn't available yet. */
  private _computePosition(height: number): { top: number; left: number } | null {
    const el = this.props.anchor.current;
    if (!el) return null;

    const popupW = Math.min(MemoryDistributionPopup.POPUP_WIDTH, window.innerWidth - MemoryDistributionPopup.MARGIN * 2);
    const rect = el.getBoundingClientRect();
    let left = rect.left + rect.width / 2 - popupW / 2;
    left = Math.max(MemoryDistributionPopup.MARGIN, Math.min(left, window.innerWidth - popupW - MemoryDistributionPopup.MARGIN));

    const upTop = rect.top - height - MemoryDistributionPopup.GAP;
    let top = upTop >= MemoryDistributionPopup.MARGIN ? upTop : rect.bottom + MemoryDistributionPopup.GAP;
    if (top + height > window.innerHeight - MemoryDistributionPopup.MARGIN) {
      top = Math.max(MemoryDistributionPopup.MARGIN, window.innerHeight - height - MemoryDistributionPopup.MARGIN);
    }
    return { top, left };
  }

  private _reanchor() {
    const h = this._popupRef.current?.offsetHeight ?? 0;
    if (h <= 0) return;
    const pos = this._computePosition(h);
    if (pos) this.setPos(pos);
  }

  private _onDocumentMouseDown = (e: MouseEvent) => {
    const target = e.target as Node;
    if (
      this._popupRef.current && !this._popupRef.current.contains(target) &&
      this.props.anchor.current && !this.props.anchor.current.contains(target)
    ) {
      this.props.onClose();
    }
  };

  private _onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.props.onClose();
  };

  @action
  private setPos(pos: { top: number; left: number }) {
    this._pos = pos;
  }

  @action
  private setHoverKey(key: string | null) {
    this._hoverKey = key;
  }

  render() {
    const { distribution, required, onClose } = this.props;
    const { _hoverKey, _pos, _popupRef } = this;

    const views: PoolView[] = [];
    if (distribution.gpu) {
      views.push(buildView(distribution.gpu, 'GPU · VRAM', <ImgGpu size={13} />, required));
    }
    if (distribution.ram) {
      views.push(buildView(distribution.ram, 'RAM', <ImgRam size={13} />, null));
    }

    if (views.length === 0) return null;

    const popup = (
      <div
        ref={_popupRef}
        className="memory-dist-popup"
        style={{ top: _pos.top, left: _pos.left }}
        onClick={e => e.stopPropagation()}
        onMouseLeave={() => this.setHoverKey(null)}
      >
        <div className="memory-dist-popup-header">
          <span>Memory distribution</span>
          <button className="memory-dist-popup-close" onClick={onClose} title="Close">✕</button>
        </div>
        <div className="memory-dist-popup-body">
          {views.map(view => (
            <div key={view.title} className="memory-dist-pool">
              <div className="memory-dist-pool-title">{view.icon} {view.title}</div>
              <div className="memory-dist-pool-donut">
                <MemoryDonut pool={view.pool} segments={view.segments} hoverKey={_hoverKey} onHover={key => this.setHoverKey(key)} />
              </div>
              {view.required && !view.required.fits && (
                <div className="memory-dist-required-warn">
                  <ImgWarning size={12} /> {view.required.seg.label} needs {fmtGb(view.required.seg.sizeGb)} GB — only {fmtGb(Math.max(0, view.pool.totalGb - view.pool.usedGb))} GB free
                </div>
              )}
              <ul className="memory-dist-legend">
                {groupLegendSegments(view.segments).map(entry => {
                  if (entry.type === 'header') {
                    const ctx = fmtContext(view.segments.find(s => s.model === entry.model)?.contextSize);
                    return (
                      <li key={entry.key} className="memory-dist-legend-header" title={ctx ? `${entry.model} — measured from the Ollama log at ${ctx} context` : entry.model}>
                        <span className="memory-dist-legend-label memory-dist-legend-model">{entry.model}</span>
                        <span className="memory-dist-legend-size">{fmtGb(entry.totalGb)} GB</span>
                        <span className="memory-dist-legend-pct">{view.pool.totalGb > 0 ? ((entry.totalGb / view.pool.totalGb) * 100).toFixed(0) : 0}%</span>
                      </li>
                    );
                  }
                  const seg = entry.seg;
                  const ctx = fmtContext(seg.contextSize);
                  const isOllama = seg.group === 'ollama' || seg.group.startsWith('ollama-');
                  const title = isOllama && ctx
                    ? seg.measured
                                          ? `${seg.label} — measured from the Ollama log at ${ctx} context`
                                          : `${seg.label} — includes KV cache reserved for ${ctx} context`
                                        : seg.group === 'unknown'
                                          ? 'Memory reported busy by the system but not attributable to Ollama/ComfyUI (driver, OS, other apps)'
                                          : seg.label;
                  return (
                    <li
                      key={seg.key}
                      data-key={seg.key}
                      className={`memory-dist-legend-item ${seg.model ? 'memory-dist-legend-sub' : 'memory-dist-legend-standalone'}${_hoverKey === seg.key ? ' hovered' : ''}`}
                      title={title}
                      onMouseEnter={() => this.setHoverKey(seg.key)}
                      onMouseLeave={() => this.setHoverKey(null)}
                    >
                      <span className="memory-dist-legend-dot" style={{ background: seg.color }} />
                      <span className="memory-dist-legend-label">{seg.label}</span>
                      <span className="memory-dist-legend-size">{fmtGb(seg.sizeGb)} GB</span>
                      <span className="memory-dist-legend-pct">{view.pool.totalGb > 0 ? ((seg.sizeGb / view.pool.totalGb) * 100).toFixed(0) : 0}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );

    // Portal: `position: fixed` breaks inside transformed ancestors (modal scale/animation).
    return createPortal(popup, document.body);
  }
}

export default MemoryDistributionPopup;
